import type { ConsoleSkill, ConsoleSkillDetail, ConsoleSkillFile } from '@plato/shared';
import { parseSkillFiles } from '@plato/shared/skills';
import { ConsoleApiError } from './http-client.ts';
import type { SkillsConsoleData } from './skills-data.ts';

const AT = '2026-10-14T09:00:00.000Z';

const exampleSkill = (
  id: string,
  name: string,
  description: string,
  instructions: string,
  fields: Partial<ConsoleSkill>,
): ConsoleSkillDetail => ({
  id,
  name,
  description,
  status: 'listed',
  always_on: false,
  version: 1,
  invocation_count: 0,
  created_at: AT,
  updated_at: AT,
  ...fields,
  instructions,
  files: [
    {
      path: 'SKILL.md',
      content: `---\nname: ${name}\ndescription: ${description}\n---\n${instructions}`,
    },
  ],
  versions: [{ version: fields.version ?? 1, description, created_at: AT }],
});

const demo: { skills: ConsoleSkillDetail[]; nextId: number } = {
  skills: [
    exampleSkill(
      'skl_demo1',
      'weekly-purchasing-brief',
      'Writes the weekly purchasing brief: last week’s invoice prices against public market prices. Use when the user asks for a market or purchasing brief.',
      '# Weekly purchasing brief\n\n1. Pull last week’s invoices.\n2. Compare the top 20 items with public prices.\n3. Write one page with a chart.',
      { always_on: true, invocation_count: 1284, version: 3 },
    ),
    exampleSkill(
      'skl_demo2',
      'menu-costing',
      'Works out plate cost and margin for a recipe from current invoice prices. Use when the user asks what a dish costs to make.',
      '# Menu costing\n\nPrice every ingredient from the latest invoice, then show cost per plate and margin.',
      { invocation_count: 342 },
    ),
    exampleSkill(
      'skl_demo3',
      'vendor-scorecard',
      'Rates each vendor on price changes, credits and late deliveries over a period.',
      '# Vendor scorecard\n\nDraft.',
      { status: 'draft' },
    ),
  ],
  nextId: 4,
};

const summary = ({ instructions: _i, files: _f, versions: _v, ...skill }: ConsoleSkillDetail) =>
  skill;

function find(skillId: string): ConsoleSkillDetail {
  const skill = demo.skills.find((entry) => entry.id === skillId);
  if (!skill) throw new ConsoleApiError(404, 'Skill not found.');
  return skill;
}

function parsed(files: readonly ConsoleSkillFile[]) {
  const result = parseSkillFiles(files);
  if (!result.ok) {
    throw new ConsoleApiError(
      400,
      `The folder is not a valid skill: ${result.problems.join('; ')}.`,
    );
  }
  return result.skill;
}

export const demoSkillsData: SkillsConsoleData = {
  listSkills: async () => demo.skills.map(summary),
  getSkill: async (_tenant, skillId) => structuredClone(find(skillId)),
  addSkill: async (_tenant, files) => {
    const skill = parsed(files);
    if (demo.skills.some((entry) => entry.name === skill.name)) {
      throw new ConsoleApiError(
        409,
        `A skill named "${skill.name}" is already in the marketplace.`,
      );
    }
    const created = exampleSkill(
      `skl_demo${demo.nextId++}`,
      skill.name,
      skill.description,
      skill.instructions,
      {
        status: 'draft',
      },
    );
    demo.skills.push({ ...created, files: [...skill.files] });
    return summary(created);
  },
  addSkillVersion: async (_tenant, skillId, files) => {
    const current = find(skillId);
    const skill = parsed(files);
    if (skill.name !== current.name) {
      throw new ConsoleApiError(400, `A new version must keep the name "${current.name}".`);
    }
    const version = current.version + 1;
    Object.assign(current, {
      version,
      description: skill.description,
      instructions: skill.instructions,
      files: [...skill.files],
      versions: [{ version, description: skill.description, created_at: AT }, ...current.versions],
    });
    return summary(current);
  },
  updateSkill: async (_tenant, skillId, change) => {
    const current = find(skillId);
    if (change.status !== undefined) current.status = change.status;
    if (change.always_on !== undefined) current.always_on = change.always_on;
    return summary(current);
  },
  deleteSkill: async (_tenant, skillId) => {
    find(skillId);
    demo.skills = demo.skills.filter((entry) => entry.id !== skillId);
  },
};
